import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EditNoteTwoToneIcon from "@mui/icons-material/EditNoteTwoTone";
import type { TodoList } from "../hooks/useTodos.ts";
import { SaveProjectTemplateModal } from "./SaveProjectTemplateModal.tsx";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";

interface Props {
  onClose: (settingsChanged: boolean) => void;
  allLists: TodoList[];
  onUpdateListStatus: (listId: string, status: string) => void;
  onDeleteList: (listId: string) => void;
  onUpdateListName: (listId: string, name: string) => Promise<boolean>;
  honbuId: number;
  projectPins: Map<number, Date>;
  onTogglePin: (listId: string) => Promise<void>;
  roomId?: number;
  color?: PaletteColor;
}

export function ListEditDialog({
  onClose,
  allLists,
  onUpdateListStatus,
  onDeleteList,
  onUpdateListName,
  honbuId,
  projectPins,
  onTogglePin,
  roomId,
  color = "Blue",
}: Props) {
  const accent = PaletteColorHex[color];
  const [editingList, setEditingList] = useState<{
    id: string;
    name: string;
  } | null>(null);
  // --- テンプレート保存用ステート ---
  const [templateTarget, setTemplateTarget] = useState<TodoList | null>(null);
  const handleOpenProjectTemplateSave = (list: TodoList) => {
    setTemplateTarget(list);
  };
  // --- プロジェクトテンプレート保存実行関数 ---
  const handleSaveProjectTemplate = async (
    name: string,
    isShare: boolean,
    includeCompleted: boolean,
    includeHidden: boolean
  ) => {
    if (!templateTarget) return;
    try {
      const response = await fetch(
        `/api/todolists/${templateTarget.id}/save-as-template`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            templateName: name.trim(),
            isShare: isShare,
            createdHonbuId: honbuId,
            // --- サーバーにフラグを送信 ---
            includeCompleted: includeCompleted,
            includeHidden: includeHidden,
          }),
        }
      );

      if (!response.ok) throw new Error();
      setTemplateTarget(null);
    } catch (error) {
      alert("保存に失敗しました");
    }
  };

  // ピン留め状態でソートしたアクティブリスト
  const activeLists = allLists
    .filter((list) => list.status === "Active")
    .sort((a, b) => {
      const aPinned = projectPins.has(parseInt(a.id, 10));
      const bPinned = projectPins.has(parseInt(b.id, 10));
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      if (aPinned && bPinned) {
        const aDate = projectPins.get(parseInt(a.id, 10))!;
        const bDate = projectPins.get(parseInt(b.id, 10))!;
        return aDate.getTime() - bDate.getTime();
      }
      return 0;
    });

  const hiddenLists = allLists.filter((list) => list.status !== "Active");

  const handleNameChangeCommit = async () => {
    if (!editingList || !editingList.name.trim()) {
      setEditingList(null);
      return;
    }
    const originalList = allLists.find((l) => l.id === editingList.id);
    if (originalList && originalList.name !== editingList.name.trim()) {
      await onUpdateListName(editingList.id, editingList.name.trim());
    }
    setEditingList(null);
  };

  const handlePinClick = async (e: React.MouseEvent, listId: string) => {
    e.stopPropagation();
    await onTogglePin(listId);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 sm:p-4"
      onClick={() => onClose(false)}
    >
      {templateTarget && (
        <SaveProjectTemplateModal
          initialName={templateTarget.name}
          onClose={() => setTemplateTarget(null)}
          onSave={handleSaveProjectTemplate}
          roomId={roomId}
        />
      )}
      <div
        className="bg-white w-full h-full flex flex-col shadow-2xl border-2 border-white sm:rounded-xl sm:max-w-lg sm:h-[70vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-4 py-3 flex-shrink-0"
          style={{
            background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
          }}
        >
          <div className="flex items-center gap-2 text-white">
            <EditNoteTwoToneIcon fontSize="small" />
            <span className="font-bold text-sm">プロジェクトの編集</span>
          </div>
          <button
            onClick={() => onClose(false)}
            className="p-1 rounded-full text-white hover:bg-white/20"
            aria-label="閉じる"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <div className="p-6 space-y-6 overflow-y-auto">
          {/* 表示中のプロジェクト */}
          <h4 className="font-semibold">表示中のプロジェクト</h4>
          <div className="pl-4 border-l-2 ml-2 space-y-2">
            {activeLists.map((list) => {
              const isPinned = projectPins.has(parseInt(list.id, 10));
              return (
                <div
                  key={list.id}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-md group"
                >
                  {/* ピンボタン */}
                  {/* <button
                    onClick={(e) => handlePinClick(e, list.id)}
                    className={`p-1 rounded-full mr-2 transition-opacity ${
                      isPinned
                        ? "opacity-100"
                        : "opacity-40 group-hover:opacity-70"
                    } hover:bg-gray-200`}
                    title={isPinned ? "ピン留めを解除" : "ピン留め"}
                  >
                    {isPinned ? (
                      <PushPinIcon
                        className="w-4 h-4 text-blue-600"
                        fontSize="small"
                      />
                    ) : (
                      <PushPinOutlinedIcon
                        className="w-4 h-4 text-gray-400"
                        fontSize="small"
                      />
                    )}
                  </button> */}

                  {editingList?.id === list.id ? (
                    <input
                      type="text"
                      value={editingList.name}
                      onChange={(e) =>
                        setEditingList({
                          ...editingList,
                          name: e.target.value,
                        })
                      }
                      onBlur={handleNameChangeCommit}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleNameChangeCommit();
                      }}
                      className="p-1 border rounded-md flex-grow"
                      autoFocus
                    />
                  ) : (
                    <span
                      onClick={() =>
                        setEditingList({ id: list.id, name: list.name })
                      }
                      className="cursor-pointer hover:bg-gray-200 p-1 rounded-md flex-grow"
                    >
                      {list.name}
                    </span>
                  )}
                  <div className="space-x-2 flex-shrink-0 flex items-center">
                    <button
                      onClick={() => onUpdateListStatus(list.id, "Hidden")}
                      className="text-sm text-orange-600 hover:underline"
                    >
                      非表示にする
                    </button>
                    {/* プロジェクトテンプレート化ボタン */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setTemplateTarget(list);
                      }}
                      className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                      title="プロジェクトをテンプレートとして保存"
                    >
                      <SaveAltIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 非表示のプロジェクト */}
          <h4 className="font-semibold pt-4">非表示のプロジェクト</h4>
          <div className="pl-4 border-l-2 ml-2 space-y-2">
            {hiddenLists.length > 0 ? (
              hiddenLists.map((list) => (
                <div
                  key={list.id}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                >
                  {editingList?.id === list.id ? (
                    <input
                      type="text"
                      value={editingList.name}
                      onChange={(e) =>
                        setEditingList({ ...editingList, name: e.target.value })
                      }
                      onBlur={handleNameChangeCommit}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleNameChangeCommit();
                      }}
                      className="p-1 border rounded-md flex-grow"
                      autoFocus
                    />
                  ) : (
                    <span
                      onClick={() =>
                        setEditingList({ id: list.id, name: list.name })
                      }
                      className="cursor-pointer hover:bg-gray-200 p-1 rounded-md flex-grow"
                    >
                      {list.name}
                    </span>
                  )}
                  <div className="space-x-2 flex-shrink-0 flex items-center">
                    <button
                      onClick={() => onUpdateListStatus(list.id, "Active")}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      表示する
                    </button>
                    <button
                      onClick={() => onDeleteList(list.id)}
                      className="text-sm text-red-600 hover:underline"
                    >
                      削除
                    </button>
                    {/* テンプレート化ボタンを追加 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setTemplateTarget(list);
                      }}
                      className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                      title="プロジェクトをテンプレートとして保存"
                    >
                      <SaveAltIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 p-2">
                非表示のプロジェクトはありません。
              </p>
            )}
          </div>
        </div>
        <div className="p-4 border-t flex justify-end mt-auto">
          <button
            onClick={() => onClose(false)}
            className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}
